import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { unwrapQlResult } from './ql-result.util';

/** Mirrors NotificationAPI.Core.Enums.NotificationType (string enum). */
export type NotificationType = 'Success' | 'Info' | 'Warning' | 'Error';

/** Delivery status — not the same as UI read/unread. */
export type NotificationStatus = 'Pending' | 'Sent' | 'Failed';

export type NotificationDuration = 'Short' | 'Normal' | 'Long';

/** Item returned by GET api/notification/all. */
export interface NotificationOutDto {
  id: number;
  userGuid: string;
  /** Workspace the notification belongs to; null = personal / platform-wide. */
  organizationId?: number | null;
  title: string;
  message: string;
  type: NotificationType;
  duration: NotificationDuration;
  status: NotificationStatus;
  createdAt?: string | null;
  conversationId?: number | null;
}

/** Live SignalR payload (may omit id before persistence). */
export interface NotificationLiveDto {
  id?: number;
  userGuid?: string;
  organizationId?: number | null;
  title?: string;
  message?: string;
  type?: NotificationType | number;
  duration?: NotificationDuration | number;
  status?: NotificationStatus | number;
  createdAt?: string | null;
  conversationId?: number | null;
}

@Injectable({ providedIn: 'root' })
export class NotificationApiService {
  private readonly http = inject(HttpClient);

  /**
   * Current user's notifications (newest-first when the API returns them unordered).
   *
   * The API scopes the inbox to the active workspace from the
   * `selectedOrganizationId` header the auth interceptor already puts on every
   * request — workspace scope is never a query parameter here — and always
   * includes personal / platform-wide items.
   */
  getAll(guid?: string): Observable<NotificationOutDto[]> {
    const params = guid ? `?guid=${encodeURIComponent(guid)}` : '';
    return this.http.get<unknown>(`notification/all${params}`).pipe(
      map((res) => {
        const data = unwrapQlResult<NotificationOutDto[] | null>(res);
        return Array.isArray(data) ? data : [];
      }),
    );
  }
}
